/**
 * Tax-rule changes waiting for a second look. Every add, close or delete of a tax rule
 * raises one; it stays here until someone with catalog write access marks it reviewed.
 * The change and the review both live in the audit trail -- see `catalog.tax_alerts`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { API_PREFIX, apiFetch, apiGet } from '@/api/client'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { toast } from '@/lib/toast'

export interface TaxRuleAlert {
  id: string
  tax_rule_id: string | null
  action: 'insert' | 'update' | 'delete'
  changed_at: string
  changed_by: string | null
  summary: string
  affected_products: number
  reviewed_at: string | null
  review_note: string | null
}

export const listTaxRuleAlerts = () =>
  apiGet<TaxRuleAlert[]>(`${API_PREFIX}/tax-rules/alerts?pending=true`)

export const acknowledgeTaxRuleAlert = (id: string, note: string) =>
  apiFetch<void>(`${API_PREFIX}/tax-rules/alerts/${id}/acknowledge`, {
    method: 'POST',
    body: JSON.stringify({ note: note || null }),
  })

const ACTION_LABEL: Record<TaxRuleAlert['action'], string> = {
  insert: 'Added',
  update: 'Changed',
  delete: 'Deleted',
}

export function TaxRuleAlerts() {
  const queryClient = useQueryClient()
  const [notes, setNotes] = useState<Record<string, string>>({})
  // Under the page's own key, so anything that refreshes the rules refreshes this too.
  const alerts = useQuery({
    queryKey: ['tax-rules', 'alerts'],
    queryFn: listTaxRuleAlerts,
    retry: false,
  })
  const acknowledge = useMutation({
    mutationFn: (id: string) => acknowledgeTaxRuleAlert(id, notes[id] ?? ''),
    onSuccess: () => {
      toast.success('Marked as reviewed')
      void queryClient.invalidateQueries({ queryKey: ['tax-rules', 'alerts'] })
    },
    onError: () => toast.error('Could not record the review'),
  })

  const rows = alerts.data ?? []
  if (!rows.length) return null

  return (
    <Card className="border-amber-300 dark:border-amber-500/40">
      <CardHeader
        title={`${rows.length} tax-rule change${rows.length === 1 ? '' : 's'} need review`}
        description="Tax changes re-price every document raised after they take effect. Check each against the official notice, then mark it reviewed."
      />
      <ul role="list" className="divide-y divide-slate-200 dark:divide-slate-800">
        {rows.map((alert) => (
          <li key={alert.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-900 dark:text-slate-100">
                <span className="mr-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-500/10 dark:text-amber-400">
                  {ACTION_LABEL[alert.action]}
                </span>
                {alert.summary}
              </p>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                {new Date(alert.changed_at).toLocaleString()} · affects {alert.affected_products}{' '}
                product{alert.affected_products === 1 ? '' : 's'}
              </p>
            </div>
            <Input
              aria-label="Review note"
              placeholder="Note (optional)"
              className="w-56"
              value={notes[alert.id] ?? ''}
              onChange={(event) => setNotes({ ...notes, [alert.id]: event.target.value })}
            />
            <Button
              size="sm"
              variant="secondary"
              loading={acknowledge.isPending && acknowledge.variables === alert.id}
              onClick={() => acknowledge.mutate(alert.id)}
            >
              Mark reviewed
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  )
}
